import React from 'react';
import { Drawer } from '../../../../../../../ui/components/Common/Drawer/Drawer';
import { CCAConfig } from '../../../../../../../utils/types/cca_types';
import {
  CCASubdomain,
  CCA_SUBDOMAINS,
} from '../../../../../../questions/CCABank/ccaConstants';
import { useManualSelection } from '../hooks/useManualSelection';
import { getSubdomainColor, toTitleCase } from '../utils/ccaHelpers';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  jobOpeningId: string;
  existingConfig?: CCAConfig;
}

const ChevronDown = ({ className }: { className?: string }) => (
  <svg className={className} fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
    <path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
  </svg>
);

export const ManualSelectionDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  jobOpeningId,
  existingConfig,
}) => {
  const {
    searchText,
    setSearchText,
    filterSubdomain,
    setFilterSubdomain,
    filterLevel,
    setFilterLevel,
    filterType,
    setFilterType,
    page,
    setPage,
    selected,
    expandedIds,
    isSaving,
    isPreloading,
    isListLoading,
    visibleQuestions,
    total,
    totalPages,
    resetPage,
    toggleExpand,
    addQuestion,
    removeQuestion,
    clearSelected,
    handleSave,
  } = useManualSelection({ jobOpeningId, existingConfig });

  const filterSelectClass =
    'rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-300';

  const footer =
    selected.length > 0 ? (
      <button
          type='button'
          disabled={isSaving}
          onClick={handleSave}
          className='w-full rounded-lg bg-emerald-600 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60'
      >
        { isSaving ? (
          <span className='flex items-center justify-center gap-2'>
            <span className='h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent' />
            Saving…
          </span>
        ) : (
          `Save ${selected.length} Question${selected.length !== 1 ? 's' : ''}`
        ) }
      </button>
    ) : undefined;

  return (
    <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title='Manual Selection'
        footerContent={footer}
        width='w-full md:w-2/3'
    >
      <div className='flex gap-4'>

        { /* ── Left: Browse panel ── */ }
        <div className='flex-1 min-w-0'>
          { /* Filters */ }
          <div className='mb-3 flex flex-wrap gap-2'>
            <div className='relative min-w-[160px] flex-1'>
              <svg
                  className='absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth={2}
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M21 21l-4.35-4.35m0 0A7.5 7.5 0 1 0 4.5 4.5a7.5 7.5 0 0 0 12.15 12.15z'
                />
              </svg>
              <input
                  type='text'
                  placeholder='Search questions…'
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className='w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-300'
              />
            </div>
            <select
                value={filterSubdomain}
                onChange={(e) => { setFilterSubdomain(e.target.value as CCASubdomain | 'All'); resetPage(); }}
                className={filterSelectClass}
            >
              <option value='All'>All Subdomains</option>
              { CCA_SUBDOMAINS.map((sd) => (
                <option key={sd} value={sd}>{ sd }</option>
              )) }
            </select>
            <select
                value={filterLevel}
                onChange={(e) => { setFilterLevel(e.target.value === 'All' ? 'All' : Number(e.target.value)); resetPage(); }}
                className={filterSelectClass}
            >
              <option value='All'>All Levels</option>
              { Array.from({ length: 10 }, (_, i) => i + 1).map((l) => (
                <option key={l} value={l}>Level { l }</option>
              )) }
            </select>
            <select
                value={filterType}
                onChange={(e) => { setFilterType(e.target.value); resetPage(); }}
                className={filterSelectClass}
            >
              <option value='All'>All Types</option>
              <option value='multiple_choice'>MC</option>
              <option value='short_answer'>SA</option>
            </select>
          </div>

          { /* Question list */ }
          <div className='pr-1'>
            { isListLoading ? (
              <div className='space-y-1.5'>
                { Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className='h-12 w-full animate-pulse rounded-lg bg-slate-100' />
                )) }
              </div>
            ) : visibleQuestions.length === 0 ? (
              <div className='py-10 text-center text-sm text-slate-400'>
                No questions match filters
              </div>
            ) : (
              visibleQuestions.map((q) => {
                const isExpanded = expandedIds.has(q.id);
                return (
                  <div key={q.id} className='mb-1.5 rounded-lg border border-slate-200 bg-white'>
                    <div className='flex items-center gap-2 px-3 py-2'>
                      <button
                          type='button'
                          onClick={() => toggleExpand(q.id)}
                          className='rounded p-0.5 text-slate-400 hover:bg-slate-100'
                      >
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                      <div className='min-w-0 flex-1'>
                        <p className='truncate text-xs text-slate-800'>{ q.question.text }</p>
                        <div className='mt-0.5 flex gap-1.5'>
                          <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${getSubdomainColor(q.subdomain)}`}>
                            { toTitleCase(q.subdomain).split(' ')[0] }
                          </span>
                          <span className='rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500'>
                            Lvl { q.level }
                          </span>
                          <span className='rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-600'>
                            { q.questionType === 'multiple_choice' ? 'MC' : 'SA' }
                          </span>
                        </div>
                      </div>
                      <button
                          type='button'
                          onClick={() => addQuestion(q)}
                          className='flex shrink-0 items-center gap-1 rounded-lg bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100'
                      >
                        <svg className='h-3 w-3' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
                          <path strokeLinecap='round' strokeLinejoin='round' d='M12 4.5v15m7.5-7.5h-15' />
                        </svg>
                        Add
                      </button>
                    </div>
                    { isExpanded && (
                      <div className='border-t border-slate-100 px-4 py-2'>
                        <p className='text-xs font-semibold text-slate-500'>Ideal Answer:</p>
                        <p className='mt-0.5 text-xs text-slate-700'>{ q.idealAnswer }</p>
                      </div>
                    ) }
                  </div>
                );
              })
            ) }
          </div>

          { /* Pagination */ }
          { !isListLoading && totalPages > 1 && (
            <div className='mt-2 flex items-center justify-between border-t border-slate-100 pt-2'>
              <p className='text-xs text-slate-500'>
                Page { page } of { totalPages } ({ total.toLocaleString() } total)
              </p>
              <div className='flex gap-1'>
                <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className='flex h-6 w-6 items-center justify-center rounded border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40'
                >
                  <ChevronDown className='h-3 w-3 rotate-90' />
                </button>
                <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className='flex h-6 w-6 items-center justify-center rounded border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40'
                >
                  <ChevronDown className='h-3 w-3 -rotate-90' />
                </button>
              </div>
            </div>
          ) }
        </div>

        { /* ── Right: Selected panel ── */ }
        <div className='w-56 shrink-0'>
          <div className='mb-3 flex items-center justify-between'>
            <p className='text-xs font-semibold text-slate-700'>
              Selected ({ selected.length })
            </p>
            { selected.length > 0 && (
              <button
                  type='button'
                  onClick={clearSelected}
                  className='text-xs text-rose-500 transition-colors hover:text-rose-600'
              >
                Clear all
              </button>
            ) }
          </div>

          { selected.length === 0 ? (
            <div className='flex min-h-[200px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 text-slate-400'>
              { isPreloading ? (
                <>
                  <div className='mb-2 h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-transparent' />
                  <p className='text-xs text-slate-500'>Loading saved…</p>
                </>
              ) : (
                <p className='text-xs'>No questions selected</p>
              ) }
            </div>
          ) : (
            <div className='space-y-1.5'>
              { selected.map((q, i) => (
                <div key={q.id} className='flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2'>
                  <span className='mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-[10px] font-bold text-emerald-700'>
                    { i + 1 }
                  </span>
                  <div className='min-w-0 flex-1'>
                    <p className='line-clamp-2 text-xs text-slate-700'>{ q.question.text }</p>
                    <span className={`mt-0.5 inline-block rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${getSubdomainColor(q.subdomain)}`}>
                      { toTitleCase(q.subdomain).split(' ')[0] }
                    </span>
                  </div>
                  <button
                      type='button'
                      onClick={() => removeQuestion(q.id)}
                      className='shrink-0 rounded p-0.5 text-rose-400 transition-colors hover:bg-rose-50 hover:text-rose-600'
                  >
                    <svg className='h-3.5 w-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                      <path strokeLinecap='round' strokeLinejoin='round' d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                    </svg>
                  </button>
                </div>
              )) }
            </div>
          ) }
        </div>

      </div>
    </Drawer>
  );
};
